import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FiFileText, FiTool, FiBox, FiCpu, FiDatabase, FiCheckCircle, FiTarget, FiAnchor, FiNavigation, FiSend, FiActivity, FiHome } from 'react-icons/fi';

const iconMap = {
  fileText: FiFileText,
  tool: FiTool,
  box: FiBox,
  cpu: FiCpu,
  database: FiDatabase,
  checkCircle: FiCheckCircle,
};

// Strategic sectors icons
const sectorIconMap = {
  radar: FiTarget,
  naval: FiAnchor,
  airborne: FiNavigation,
  missile: FiSend,
  test: FiActivity,
  psu: FiHome,
};

function DomainsPanel() {
  const { t } = useTranslation();

  // Strategic sectors data - get from translations
  const strategicSectorsTitle = t('strategicSectors.title', { defaultValue: "Serving India's Strategic Sectors" });
  const strategicSectorsSubtitle = t('strategicSectors.subtitle', { defaultValue: 'HADEN delivers mission-critical systems across:' });
  
  const translatedSectors = t('strategicSectors.sectors', { returnObjects: true });
  const defaultSectors = [
    { id: 'radar', icon: 'radar', name: 'Ground Radar & Surveillance' },
    { id: 'naval', icon: 'naval', name: 'Naval Platforms & Shipboard' },
    { id: 'airborne', icon: 'airborne', name: 'Airborne & Support Equipment' },
    { id: 'missile', icon: 'missile', name: 'Missile Ground Support' },
    { id: 'test', icon: 'test', name: 'Test & Evaluation Facilities' },
    { id: 'psu', icon: 'psu', name: 'Defence PSU Programs' },
  ];
  
  const strategicSectors = Array.isArray(translatedSectors) ? translatedSectors : defaultSectors;

  // Get translated content

  // Get translated content
  const title = t('enablers.title', { defaultValue: 'In-House Engineering Enablers (How Delivery Stays Predictable)' });
  const subtitle = t('enablers.subtitle', { defaultValue: 'Delivered systems are enabled by disciplined translation of customer requirements into controlled design, controlled build, and controlled evidence — with traceability retained from requirement interpretation through acceptance readiness.' });

  // 6 Enabler Cards (A-F) - Card E split into E and F
  const defaultEnablers = [
    {
      id: 'A',
      name: 'Spec Translation & Architecture Visualization',
      icon: 'fileText',
      oneLiner: 'Requirements are matured into a coherent system mental model before CAD execution, reducing late churn and improving acceptance predictability.',
      bullets: [
        'Deep requirement interpretation + usability thinking beyond literal spec text (program dependent)',
        'Architecture formation before tool-driven detailing, enabling cleaner integration decisions',
        'CAD modeling used to lock interfaces, service access logic, and manufacturability intent early'
      ]
    },
    {
      id: 'B',
      name: 'CAD-to-CAM Manufacturing Readiness',
      icon: 'tool',
      oneLiner: 'Manufacturing is executed with controlled workmanship intent — enabling repeatability, traceability, and predictable integration.',
      bullets: [
        'CAM-aligned fabrication planning, fitment discipline, and controlled build practices',
        'Workmanship controls: routing, access planning, mounting integrity, and serviceability provisions',
        'Build traceability retained for as-built configuration summary and acceptance readiness'
      ]
    },
    {
      id: 'C',
      name: 'Structures, Sheet Metal & System Packaging',
      icon: 'box',
      oneLiner: 'Enclosures, frames, skids, and housings are produced in-house to maintain dimensional control and integration readiness.',
      bullets: [
        'Packaging engineered for transport, handling, and field constraints (program dependent)',
        'Maintainability-first layouts with access discipline and protection intent',
        'Integration-ready fabrication supporting predictable assembly and testing'
      ]
    },
    {
      id: 'D',
      name: 'Harnessing & Electrical Workmanship Control',
      icon: 'cpu',
      oneLiner: 'Wiring and harnessing are executed with traceable workmanship discipline aligned to field reliability.',
      bullets: [
        'Harness design and build discipline (routing, identification, workmanship controls)',
        'Protection and grounding philosophy aligned to EMI/EMC readiness (as applicable)',
        'Testability provisions embedded for diagnostics and verification evidence'
      ]
    },
    {
      id: 'E',
      name: 'HADEN ATLAS — Requirements-to-Baseline Traceability',
      icon: 'database',
      oneLiner: 'Execution is coordinated through an AS9100D-aligned digital backbone that links customer requirements to controlled engineering definition and released baselines.',
      bullets: [
        'Requirement capture and decomposition (spec → customer requirement table/CR structure) with traceability retained',
        'Engineering definition linkage (requirements → design intent → bill of materials) with controlled baselines',
        'Planning and procurement traceability supporting repeatable execution and configuration integrity',
        'Documentation linkage supporting acceptance readiness (as applicable: drawings, lists, plans)'
      ]
    },
    {
      id: 'F',
      name: 'HADEN ATLAS — Change Control & Evidence Pack Readiness',
      icon: 'checkCircle',
      oneLiner: 'Configuration changes and acceptance evidence are controlled so as-built integrity and closure artifacts remain audit-ready and program-aligned.',
      bullets: [
        'Change control discipline with configuration traceability (ECR/ECO flows, approvals, baselines)',
        'As-built configuration integrity (what changed, why, and how it was closed)',
        'Evidence pack readiness support (test logs, verification closure artifacts, traceability snapshots)',
        'Record discipline aligned to AS9100D expectations (retention and retrievability as applicable)'
      ]
    }
  ];

  // Get translated enabler cards
  const translatedCards = t('enablers.cards', { returnObjects: true });

  // Use translated cards if available, otherwise defaults
  const enablers = Array.isArray(translatedCards) && translatedCards.length === 6 
    ? translatedCards.map((card, i) => ({
        ...card,
        id: defaultEnablers[i]?.id || `card-${i}`,
        icon: defaultEnablers[i]?.icon || 'fileText'
      })) 
    : defaultEnablers;

  return (
    <div 
      id="enablers" 
      className="panel panel-light scroll-mt-20" 
      data-testid="enablers-panel"
    >
      <div className="panel-content">
        {/* Strategic Sectors Strip - NEW - Above Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="mb-12 p-8 rounded-xl text-center"
          style={{ 
            backgroundColor: 'rgba(16, 25, 87, 0.03)',
            border: '1px solid rgba(16, 25, 87, 0.08)'
          }}
          data-testid="strategic-sectors-strip"
        >
          <h3 
            className="text-xl md:text-2xl font-bold mb-2"
            style={{ color: '#101957' }}
          >
            {strategicSectorsTitle}
          </h3>
          <p 
            className="text-sm mb-6"
            style={{ color: '#475569' }}
          >
            {strategicSectorsSubtitle}
          </p>
          
          {/* 6 Sector Tiles - 2 rows on mobile, 1 row on desktop */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {strategicSectors.map((sector, index) => {
              const SectorIcon = sectorIconMap[sector.icon] || FiTarget;
              return (
                <motion.div
                  key={sector.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  className="flex flex-col items-center p-4 rounded-lg bg-white"
                  style={{ 
                    border: '1px solid rgba(16, 25, 87, 0.08)',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)'
                  }}
                >
                  <div 
                    className="w-12 h-12 rounded-full flex items-center justify-center mb-2"
                    style={{ backgroundColor: 'rgba(16, 25, 87, 0.08)' }}
                  >
                    <SectorIcon className="text-xl" style={{ color: '#101957' }} />
                  </div>
                  <span 
                    className="text-xs font-semibold text-center leading-tight"
                    style={{ color: '#334155' }}
                  >
                    {sector.name}
                  </span>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="text-center mb-10"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 heading-premium">
            {title}
          </h2>
          <p className="text-base md:text-lg max-w-3xl mx-auto subheading-premium text-left">
            {subtitle}
          </p>
        </motion.div>

        {/* 6 Enabler Cards Grid - 3 columns x 2 rows */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {enablers.map((enabler, index) => {
            const Icon = iconMap[enabler.icon] || FiFileText;
            return (
              <motion.div
                key={enabler.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, ease: "easeOut", delay: index * 0.05 }}
                className="card-premium p-6"
                data-testid={`enabler-card-${enabler.id}`}
              >
                {/* Header with icon and title */}
                <div className="flex items-start gap-4 mb-3">
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: 'rgba(16, 25, 87, 0.08)' }}
                  >
                    <Icon className="text-xl" style={{ color: '#101957' }} />
                  </div>
                  <h3 className="text-lg font-bold leading-tight text-left" style={{ color: '#101957' }}>
                    {enabler.name}
                  </h3>
                </div>

                {/* One-liner with left alignment */}
                <p className="text-sm mb-4 leading-relaxed text-left" style={{ color: '#475569' }}>
                  {enabler.oneLiner}
                </p>

                {/* Bullets with left alignment */}
                {enabler.bullets && enabler.bullets.length > 0 && (
                  <ul className="space-y-2">
                    {enabler.bullets.map((bullet, bulletIndex) => (
                      <li 
                        key={bulletIndex} 
                        className="flex items-start text-sm"
                        style={{ color: '#64748b' }}
                      >
                        <span 
                          className="mr-2 mt-1.5 w-1 h-1 rounded-full flex-shrink-0"
                          style={{ backgroundColor: '#101957' }}
                        />
                        <span className="leading-relaxed text-left">{bullet}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default DomainsPanel;
